import React, { useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';

type Project = { id: number; name: string };
type ExperimentRow = {
  id: number;
  metric: string;
  product: string;
  variantA?: string | null;
  variantB?: string | null;
  change?: string | null;
};
type Experiment = {
  id: number;
  name: string;
  rcId?: string | null;
  daysRunning?: number | null;
  url?: string | null;
  project: Project;
  rows: ExperimentRow[];
};

function App() {
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [availableDates, setAvailableDates] = useState<{ date: string; count: number }[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [openIds, setOpenIds] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [lastScrapeAt, setLastScrapeAt] = useState<number | null>(null);
  const [now, setNow] = useState<number>(Date.now());
  const [sort, setSort] = useState<{ key: 'days' | 'ltv' | 'mrr' | 'mrrRenew'; dir: 'asc' | 'desc' } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        // Load available dates first
        const datesRes = await fetch(`${API_BASE_URL}/experiments/dates`);
        if (!datesRes.ok) throw new Error(`HTTP ${datesRes.status}`);
        const dates = (await datesRes.json()) as { date: string; count: number }[];
        setAvailableDates(dates);

        const defaultDate = dates.length > 0 ? dates[0].date : '';
        setSelectedDate(defaultDate);

        // Load experiments for the chosen date (or latest by default)
        await fetchForDate(defaultDate);
        setLastScrapeAt(Date.now());
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const since = lastScrapeAt ? Math.floor((now - lastScrapeAt) / 1000) : null;
  const formatSince = (s: number | null) => {
    if (s == null) return '—';
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  };

  const fetchForDate = async (date: string) => {
    const url = date ? `${API_BASE_URL}/experiments?date=${encodeURIComponent(date)}` : `${API_BASE_URL}/experiments`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as Experiment[];
    setExperiments(data);
  };

  const onForceScrape = async () => {
    try {
      await fetch(`${API_BASE_URL}/experiments/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ headless: true, debug: true }),
      });
      // Refresh data after triggering
      setLoading(true);
      // Reload dates and experiments for current selection
      const datesRes = await fetch(`${API_BASE_URL}/experiments/dates`);
      const dates = (await datesRes.json()) as { date: string; count: number }[];
      setAvailableDates(dates);
      const nextDate = selectedDate || (dates[0]?.date ?? '');
      await fetchForDate(nextDate);
      setLastScrapeAt(Date.now());
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  // Client-side deduplication as a safety net
  const deduped = (() => {
    const map = new Map<string, Experiment>();
    for (const exp of experiments) {
      const key = `${exp.project.id}::${exp.rcId ?? exp.name}`;
      if (!map.has(key)) map.set(key, exp);
    }
    return Array.from(map.values());
  })();

  const sorted = [...deduped].sort((a, b) => {
    if (!sort) return 0;
    const metric = (key: typeof sort.key) => {
      if (key === 'days') return [(a.daysRunning ?? -Infinity), (b.daysRunning ?? -Infinity)];
      const get = (exp: Experiment, name: string) => parseChange(getMetricRow(exp.rows, name)?.change) ?? -Infinity;
      if (key === 'ltv') return [get(a, 'Realized LTV per customer'), get(b, 'Realized LTV per customer')];
      if (key === 'mrr') return [get(a, 'Total MRR'), get(b, 'Total MRR')];
      return [get(a, 'Total MRR (set to renew)'), get(b, 'Total MRR (set to renew)')];
    };
    const [av, bv] = metric(sort.key);
    if (av === bv) return 0;
    return sort.dir === 'asc' ? (av < bv ? -1 : 1) : (av > bv ? -1 : 1);
  });

  const toggleSort = (key: 'days' | 'ltv' | 'mrr' | 'mrrRenew') => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'desc' };
      return { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
    });
  };

  const toggleOpen = (id: number) => {
    setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getMetricRow = (rows: ExperimentRow[], metricName: string): ExperimentRow | undefined => {
    return rows.find((r) => r.metric === metricName && r.product === 'All');
  };

  const parseChange = (value?: string | null): number | null => {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const formatPercent = (n: number | null): string => {
    if (n == null) return '';
    return `${(n * 100).toFixed(2)}%`;
  };

  if (loading) return <div style={{ padding: 20 }}>Loading…</div>;
  if (error) return <div style={{ padding: 20, color: 'red' }}>Error: {error}</div>;

  return (
    <div style={{ padding: 20 }}>
      <h1>RevenueCat Experiments</h1>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '8px 0 16px' }}>
        <input
          placeholder="Search project or experiment"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ padding: 8, flex: '0 1 320px' }}
        />
        <select
          value={selectedDate}
          onChange={async (e) => {
            const d = e.target.value;
            setSelectedDate(d);
            setLoading(true);
            try {
              await fetchForDate(d);
            } finally {
              setLoading(false);
            }
          }}
          style={{ padding: '8px 12px' }}
        >
          {availableDates.map((d) => (
            <option key={d.date} value={d.date}>{d.date}</option>
          ))}
        </select>
        <div style={{ color: '#333' }}>Experiments: {sorted.length}</div>
        <button onClick={onForceScrape} style={{ padding: '8px 12px' }}>Force scrape</button>
        <div style={{ color: '#666' }}>Last scrape: {formatSince(since)} ago</div>
      </div>
      {experiments.length === 0 ? (
        <div>No experiments yet.</div>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Project</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Experiment</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', cursor: 'pointer' }} onClick={() => toggleSort('days')}>Days {sort?.key === 'days' ? (sort.dir === 'desc' ? '▼' : '▲') : ''}</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Link</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', cursor: 'pointer' }} onClick={() => toggleSort('ltv')}>LTV/cust Δ {sort?.key === 'ltv' ? (sort.dir === 'desc' ? '▼' : '▲') : ''}</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', cursor: 'pointer' }} onClick={() => toggleSort('mrr')}>MRR Δ {sort?.key === 'mrr' ? (sort.dir === 'desc' ? '▼' : '▲') : ''}</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', cursor: 'pointer' }} onClick={() => toggleSort('mrrRenew')}>MRR renew Δ {sort?.key === 'mrrRenew' ? (sort.dir === 'desc' ? '▼' : '▲') : ''}</th>
            </tr>
          </thead>
          <tbody>
            {sorted
              .filter((exp) =>
                (exp.project.name + ' ' + exp.name).toLowerCase().includes(query.toLowerCase()),
              )
              .map((exp) => {
                const ltv = getMetricRow(exp.rows, 'Realized LTV per customer');
                const mrr = getMetricRow(exp.rows, 'Total MRR');
                const mrrRenew = getMetricRow(exp.rows, 'Total MRR (set to renew)');
                const ltvChange = parseChange(ltv?.change);
                const mrrChange = parseChange(mrr?.change);
                const mrrRenewChange = parseChange(mrrRenew?.change);
                return (
                  <React.Fragment key={exp.id}>
                    <tr
                      onClick={() => toggleOpen(exp.id)}
                      style={{ cursor: 'pointer', background: openIds[exp.id] ? '#fafafa' : undefined }}
                    >
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{exp.project.name}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{exp.name}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{exp.daysRunning ?? '—'}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                        {exp.url ? (
                          <a href={exp.url} target="_blank" rel="noreferrer">Open</a>
                        ) : null}
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee', color: ltvChange == null ? undefined : ltvChange > 0 ? 'green' : ltvChange < 0 ? 'red' : undefined }}>{formatPercent(ltvChange)}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee', color: mrrChange == null ? undefined : mrrChange > 0 ? 'green' : mrrChange < 0 ? 'red' : undefined }}>{formatPercent(mrrChange)}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee', color: mrrRenewChange == null ? undefined : mrrRenewChange > 0 ? 'green' : mrrRenewChange < 0 ? 'red' : undefined }}>{formatPercent(mrrRenewChange)}</td>
                    </tr>
                    {openIds[exp.id] && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0 }}>
                          <div style={{ padding: 12 }}>
                            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Metric</th>
                                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Product</th>
                                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Variant A</th>
                                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Variant B</th>
                                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Change</th>
                                </tr>
                              </thead>
                              <tbody>
                                {exp.rows.map((row) => (
                                  <tr key={row.id}>
                                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>{row.metric}</td>
                                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>{row.product}</td>
                                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>{row.variantA ?? ''}</td>
                                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>{row.variantB ?? ''}</td>
                                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>{row.change ?? ''}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default App;
